/**
 * @module RebLogger
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// tslint:disable:no-console

import { LoggerOptions } from './logger-options';
import { LogMessage, MessageInterface, ComponentInterface } from './log-message';
import { LOGLEVEL, MAXSTRMATCH } from './logger-const';

/**
 * RebLogger class to provide a logger functionality
 * @module RebLogger
 * @access public
 */
export class RebLogger {
  // provide initial config in case of no option provided
  private loggerOption: LoggerOptions = {
    isEnable: true, //
    output: 'BOTH', // whether log in console should visible
    level: LOGLEVEL.DEFAULT, // Levels: ALL, VERBOSE, DEFAULT, OFF
    consoleLevel: LOGLEVEL.ALL,
    batchSize: 1,
    endPoint: 'http://rebelliondev-dev02.apigee.net/common/v1/log/ui',
    sendBufferLevel: LOGLEVEL.OFF,
    bufferSize: 0,
    storeBufferLevel: LOGLEVEL.ALL,
    // mask: ['(VISA:)(\\d+)', '(Mastercard:)(\\d+)'], // Mask sensitive information as per regular Expression
    debounceThersold: 10, // Debounce thershold after which same message will get discarded
  };
  private requestId: string;
  private dateString: string;
  private logCounter: number = 0;
  private buffer: LogMessage[] = [];
  private batchBuffer: LogMessage[] = [];
  private tempLogArr: string[] = [];
  private checkCounter: boolean[] = [];
  private reloadTimer;

  constructor(
    private options: any,
    private loggerName: string
  ) {
    this.setLoggerOption(options);
    this.dateString = new Date().getTime().toString();
    this.requestId = this.dateString + '_' + this.logCounter;
    if (this.loggerOption.reloadInterval) {
      this.startReloadTimer();
    }
  }

  /**
   * trace method : similiar to console.trace
   */
  public rebTrace(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.ALL),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'TRACE',
      errorStack
    );
  }

  /**
   * info method : similiar to console.info
   */
  public rebInfo(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.ALL),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'INFO',
      errorStack
    );
  }

  /**
   * debug method : similiar to console.debug
   */
  public rebDebug(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.VERBOSE),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'DEBUG',
      errorStack
    );
  }

  /**
   * warning method : similiar to console.warn
   */
  public rebWarning(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.DEFAULT),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'WARNING',
      errorStack
    );
  }

  /**
   * error method : similiar to console.error
   */
  public rebError(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.DEFAULT),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'ERROR',
      errorStack
    );
  }

  /**
   * fatal method : similiar to console.error but with highest property.
   * Send to server even log level is 'OFF'
   */
  public rebFatal(
    moduleName: string,
    componentName: string,
    errorDesc: string,
    logInfo: object,
    errorCode: string = 'NA',
    errorStack: any = {}
  ): void {
    this.processLog(
      this.convertLevelType(LOGLEVEL.OFF),
      moduleName,
      componentName,
      errorCode,
      errorDesc,
      logInfo,
      'FATAL',
      errorStack
    );
  }

  /**
   * setLoggerOption method : Use to provide configuration to logger
   * @param provide all option or used default
   */
  public setLoggerOption(options: any) {
    this.copyProperty('isEnable', options, this.loggerOption);
    this.copyProperty('output', options, this.loggerOption);
    this.copyProperty('level', options, this.loggerOption);
    this.copyProperty('consoleLevel', options, this.loggerOption);
    this.copyProperty('batchSize', options, this.loggerOption);
    this.copyProperty('endPoint', options, this.loggerOption);
    this.copyProperty('sendBufferLevel', options, this.loggerOption);
    this.copyProperty('bufferSize', options, this.loggerOption);
    this.copyProperty('storeBufferLevel', options, this.loggerOption);
    this.copyProperty('disallow', options, this.loggerOption);
    this.copyProperty('mask', options, this.loggerOption);
    this.copyProperty('debounceThersold', options, this.loggerOption);
    this.copyProperty('beforeSend', options, this.loggerOption);
    this.copyProperty('reloadInterval', options, this.loggerOption);
    this.copyProperty('optionApi', options, this.loggerOption);
  }

  public getBuffer(): any {
    return this.buffer;
  }
  public getBatch(): any {
    return this.batchBuffer;
  }

  /**
   * processLog sendBatch :  Send log to server
   * @param provide log batch array
   */

  public sendBatch(logBatch, endPoint): void {
    let logObject = {};
    logObject['lg'] = logBatch;
    let xhr: XMLHttpRequest = new XMLHttpRequest();
    xhr.open('POST', this.loggerOption.endPoint);
    xhr.setRequestHeader('Content-Type', 'application/json');
    // call beforesend method if exist
    if (this.loggerOption.beforeSend && typeof this.loggerOption.beforeSend === 'function') {
      this.loggerOption.beforeSend.call(this, xhr);
    }
    xhr.send(logObject as XMLHttpRequestBodyInit);
  }

  /**
   * reloadOptionInit method : initiate timeout for option reset
   */
  public reloadOptionInit(): void {
    if (this.loggerOption.reloadInterval) {
      this.startReloadTimer();
    }
  }

  /**
   * Function to generate a pseudo-random number
   */
  private getPseudoRandomNumber() {
    return Math.floor(Math.random() * 10000);
  }

  /**
   * processLog method : perform all buiseness logic and make decision to store or send log
   * @param level, Module Name, Component name, eror code, error desc, error stack, console type
   */
  private processLog(
    level: number,
    moduleName: string,
    componentName: string,
    errorCode: string,
    errorDesc: string,
    logInfo: object,
    consoleType: string,
    errorStack: any
  ) {
    let isErrorObject: boolean = false;
    // send to console and do not process log if output option is only 'CONSOLE';
    if (!this.loggerOption.isEnable) {
      return;
    }
    if (level >= this.convertLevelType(this.loggerOption.consoleLevel)) {
      if (this.loggerOption.output === 'CONSOLE') {
        this.logToConsole(moduleName, componentName, errorCode, errorDesc, errorStack, consoleType);
        return;
      } else if (this.loggerOption.output === 'BOTH') {
        this.logToConsole(moduleName, componentName, errorCode, errorDesc, errorStack, consoleType);
      }
    }

    this.requestId =
      this.dateString +
      '_' +
      this.logCounter++ +
      '_' +
      this.getPseudoRandomNumber().toString().padStart(4, '0');

    if (errorStack && typeof errorStack === 'object') {
      if (errorStack.originalStack) {
        errorStack = this.stringifyLogObject(errorStack.originalStack);
      } else if (errorStack.stack) {
        errorStack = this.stringifyLogObject(errorStack.stack);
      } else {
        this.stringifyLogObject(errorStack);
      }
      isErrorObject = true;
    } else {
      errorStack = this.stringifyLogObject(errorStack);
    }
    const uiLogObject: ComponentInterface = {
      stack: errorStack,
    };
    const logMessage: MessageInterface = {
      module_name: moduleName,
      component_name: componentName,
      code: errorCode,
      description: errorDesc,
      log_info: logInfo,
      event_date: this.dateString,
      service_transaction_id: this.requestId,
      event_id: this.requestId,
    };
    const pipeSepratedLog = this.addPipeSeperator(logMessage, '||');
    let logItem: LogMessage = {
      logLevel: consoleType,
      sourceType: this.loggerName,
      attachment: uiLogObject,
      logMessage: pipeSepratedLog,
    };
    if (this.loggerOption.debounceThersold > 1) {
      let tempLogItem: string = moduleName + componentName + errorCode + errorDesc + errorStack;
      if (tempLogItem.length > MAXSTRMATCH) {
        tempLogItem = tempLogItem.substr(0, MAXSTRMATCH);
      }
      this.tempLogArr.push(tempLogItem);
      tempLogItem = null; // garbage collect
      if (!this.isUniqueLog()) {
        return;
      }
    }
    let stringFiedLogObject: string = JSON.stringify(logItem);

    // return if disallow string found
    if (!this.allow(stringFiedLogObject)) {
      /*console.log('not correct data');*/ return;
    }
    if (!isErrorObject && this.loggerOption.mask && this.loggerOption.mask.length > 0) {
      logItem.logMessage = this.mask(logItem.logMessage); // change sensitive info to '*'
    }
    if (level < this.convertLevelType(this.loggerOption.storeBufferLevel)) {
      return;
    }
    if (level < this.convertLevelType(this.loggerOption.level)) {
      // Store in buffer
      if (this.loggerOption.bufferSize > 0) {
        this.buffer.push(logItem);
        // if buffer full then remove first LogItem
        if (this.buffer.length > this.loggerOption.bufferSize) {
          this.buffer.shift();
        }
      }
      return;
    }
    if (level < this.convertLevelType(this.loggerOption.sendBufferLevel)) {
      // Only log item will send but not with buffer
      this.batchBuffer.push(logItem);
    } else {
      // Send logItem and buffer log item as well
      if (this.buffer.length > 0) {
        this.batchBuffer = this.batchBuffer.concat(this.buffer);
        this.buffer.length = 0;
      }

      this.batchBuffer.push(logItem);
    }
    console.log(this.batchBuffer.length, this.loggerOption.batchSize, this.batchBuffer);
    if (this.batchBuffer.length >= this.loggerOption.batchSize) {
      if (this.loggerOption.output === 'SERVER' || this.loggerOption.output === 'BOTH') {
        this.sendBatch(this.batchBuffer, this.loggerOption.endPoint);
        this.batchBuffer.length = 0;
      }
      return;
    }
  }

  /**
   * addPipeSeperator method : add Pipe seperator between properties and values
   * @param provide MessageInterface type of object
   */
  private addPipeSeperator(obj: any, sperator: string): string {
    const keys = Object.keys(obj);
    const lastKey = keys[keys.length - 1];
    let messageStr: string = '';
    Object.keys(obj).forEach((key) => {
      if (obj[key] != null) {
        if (typeof obj[key] !== 'object') {
          if (key !== lastKey) {
            messageStr += key + '=' + obj[key] + sperator;
          } else {
            messageStr += key + '=' + obj[key];
          }
        } else {
          Object.keys(obj[key]).forEach((innerKey) => {
            if (obj[key][innerKey] != null) {
              messageStr += innerKey + '=' + obj[key][innerKey] + sperator;
            }
          });
        }
      }
    });
    return messageStr;
  }

  /**
   * stringifyLogObject method : convert json Object to string. Deal with circular dependency
   * @param provide any type of object
   */

  private stringifyLogObject(logItem: any): string {
    let cache = [];
    let finalString = '';
    finalString = JSON.stringify(logItem, function (key, value) {
      if (typeof value === 'object' && value !== null) {
        if (cache.indexOf(value) !== -1) {
          // Circular reference found, discard key
          return;
        }
        // Store value in our collection
        cache.push(value);
      }
      return value;
    });
    cache = null; // Enable garbage collection

    return finalString;
  }

  /**
   * allow method : to check whether log message consist any not allowed string
   * @param provide string type log item
   */
  private allow(stringiFiedLogItem: string): boolean {
    try {
      if (this.loggerOption.disallow && this.loggerOption.disallow.length > 0) {
        let isDisallow: boolean = true;
        this.loggerOption.disallow.forEach((regExVal) => {
          if (new RegExp(regExVal).test(stringiFiedLogItem)) {
            isDisallow = false;
          }
        });
        return isDisallow;
      }
    } catch (e) {}
    return true;
  }

  /**
   * mask method : to convert any sensitive data to '*'
   * @param provide string type log item
   */
  private mask(stringiFiedLogItem: string) {
    this.loggerOption.mask.forEach((regExVal) => {
      stringiFiedLogItem = stringiFiedLogItem.replace(new RegExp(regExVal), function (match, p1) {
        return p1 + '*';
      });
    });
    return stringiFiedLogItem;
  }

  /**
   * isUniqueLog method : use to verify if log message getting repeated
   */
  private isUniqueLog(): boolean {
    if (this.tempLogArr.length > 1) {
      if (
        this.tempLogArr[this.tempLogArr.length - 2] === this.tempLogArr[this.tempLogArr.length - 1]
      ) {
        this.checkCounter.push(true);
      } else {
        this.checkCounter = [];
        this.tempLogArr = [];
      }
    }
    if (this.checkCounter.length >= this.loggerOption.debounceThersold) {
      this.tempLogArr.pop();
      this.checkCounter.pop();
      return false;
    }
    return true;
  }

  /**
   * copyProperty method : use to override default property with user options
   * @param provide property name, user options and LoggerOptions object
   */
  private copyProperty(propertyName: string, from: any, to: any): void {
    if (from[propertyName] === undefined) {
      return;
    }
    if (from[propertyName] === null) {
      delete to[propertyName];
      return;
    }
    to[propertyName] = from[propertyName];
  }

  /**
   * convertLevelType method : convert string Level to Integer so that easily comparable
   * @param provide level in string type
   */
  private convertLevelType(level: string) {
    if (level === 'ALL') {
      return 1000;
    }
    if (level === 'VERBOSE') {
      return 2000;
    }
    if (level === 'DEFAULT') {
      return 3000;
    }
    if (level === 'OFF') {
      return 4000;
    }
    return 4000;
  }

  /**
   * logToConsole method : Use to show log message in window console
   * @param provide Log object and console type
   */

  private logToConsole(
    moduleName: string,
    componentName: string,
    errorCode: string,
    errorDesc: string,
    errorStack: any,
    consoleType: string
  ): void {
    if (consoleType === 'TRACE') {
      console.trace(
        '<<module>>: ',
        moduleName,
        '<<component>>: ',
        componentName,
        '<<error code>>: ',
        errorCode,
        '<<error detail>>: ',
        errorDesc,
        '<<error>>: ',
        errorStack
      );
    }
    if (consoleType === 'INFO') {
      console.info(
        '<<module>>: ',
        moduleName,
        '<<component>>: ',
        componentName,
        '<<error code>>: ',
        errorCode,
        '<<error detail>>: ',
        errorDesc,
        '<<error>>: ',
        errorStack
      );
    }
    if (consoleType === 'DEBUG') {
      console.debug(
        '<<module>>: ',
        moduleName,
        '<<component>>: ',
        componentName,
        '<<error code>>: ',
        errorCode,
        '<<error detail>>: ',
        errorDesc,
        '<<error>>: ',
        errorStack
      );
    }
    if (consoleType === 'WARNING') {
      console.warn(
        '<<module>>: ',
        moduleName,
        '<<component>>: ',
        componentName,
        '<<error code>>: ',
        errorCode,
        '<<error detail>>: ',
        errorDesc,
        '<<error>>: ',
        errorStack
      );
    }
    if (consoleType === 'ERROR' || consoleType === 'FATAL') {
      console.error(
        '<<module>>: ',
        moduleName,
        '<<component>>: ',
        componentName,
        '<<error code>>: ',
        errorCode,
        '<<error detail>>: ',
        errorDesc,
        '<<error>>: ',
        errorStack
      );
    }
  }

  private startReloadTimer(): void {
    this.reloadTimer = setTimeout(() => this.getOptions(), this.loggerOption.reloadInterval);
  }

  private getOptions(): void {
    clearTimeout(this.reloadTimer);
    if (this.loggerOption.optionApi) {
      let xhr = new XMLHttpRequest();
      xhr.open('GET', this.loggerOption.optionApi);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              let options = JSON.parse(xhr.responseText);
              this.setLoggerOption(options);
              this.reloadOptionInit();
            } catch (e) {
              console.log('OptionAPI options not correct', e.message);
            }
          } else {
            console.log('OptionAPI failed for logger', xhr.responseText);
          }
        }
      };
      xhr.send();
    }
  }
}
